import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TIERS = ["Reseller", "Agen", "Distributor"];

export default function PartnershipTab({ onReload }) {
  const [tier, setTier] = useState("Reseller");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverifying, setReverifying] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/partnership/${tier}`);
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  const updateStatus = async (r, status) => {
    try {
      await api.put(`/admin/partnership/${tier}/${r.id}`, { status });
      toast.success("Status diperbarui");
      load();
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reverify = async (r) => {
    setReverifying(r.id);
    try {
      const res = await api.post(`/admin/partnership/${tier}/${r.id}/reverify`);
      const url = res.data.invite_url;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link undangan baru dibuat & disalin ke clipboard (berlaku 24 jam, 1x klik)");
      } catch {
        toast.success(`Link undangan baru: ${url}`);
      }
      load();
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setReverifying("");
    }
  };

  return (
    <div className="space-y-5" data-testid="partnership-tab">
      <div className="flex gap-2 border-b border-stone-200 flex-wrap">
        {TIERS.map((t) => (
          <button
            key={t}
            onClick={() => setTier(t)}
            data-testid={`tier-tab-${t.toLowerCase()}`}
            className={`px-5 py-3 text-sm border-b-2 transition-colors ${
              tier === t
                ? "border-[#F39237] text-[#0A0A0A] font-medium"
                : "border-transparent text-stone-500 hover:text-[#0A0A0A]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="border border-stone-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-stone-50 text-left">
            <tr>
              <th className="px-4 py-3 overline text-stone-500">Nama</th>
              <th className="px-4 py-3 overline text-stone-500">WhatsApp</th>
              <th className="px-4 py-3 overline text-stone-500">Kota</th>
              <th className="px-4 py-3 overline text-stone-500">Alamat</th>
              <th className="px-4 py-3 overline text-stone-500">IG</th>
              <th className="px-4 py-3 overline text-stone-500">Pesan</th>
              <th className="px-4 py-3 overline text-stone-500">Status</th>
              <th className="px-4 py-3 overline text-stone-500">Tanggal</th>
              <th className="px-4 py-3 overline text-stone-500">Verifikasi Ulang</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-stone-500">Memuat…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-stone-500">Belum ada pendaftaran {tier}.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-stone-200" data-testid={`partner-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.full_name}</td>
                <td className="px-4 py-3">
                  <a
                    href={`https://wa.me/${(r.whatsapp || "").replace(/^0/, "62")}`}
                    target="_blank" rel="noreferrer"
                    className="text-[#F39237] hover:underline"
                  >
                    {r.whatsapp}
                  </a>
                </td>
                <td className="px-4 py-3">{r.city}</td>
                <td className="px-4 py-3 max-w-xs text-xs text-stone-600" title={r.address}>
                  <div className="truncate">{r.address || "-"}</div>
                </td>
                <td className="px-4 py-3 text-stone-500">{r.instagram}</td>
                <td className="px-4 py-3 max-w-xs truncate" title={r.message}>{r.message}</td>
                <td className="px-4 py-3">
                  <Select value={r.status} onValueChange={(v) => updateStatus(r, v)}>
                    <SelectTrigger className="w-36 h-9 rounded-none" data-testid={`status-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">
                  {new Date(r.created_at).toLocaleDateString("id-ID")}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => reverify(r)}
                    disabled={reverifying === r.id}
                    data-testid={`reverify-${r.id}`}
                    title="Buat ulang link undangan Telegram (single-use, 24 jam) tanpa isi ulang form"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-stone-300 hover:border-[#F39237] hover:text-[#F39237] transition-colors whitespace-nowrap disabled:opacity-50"
                  >
                    {reverifying === r.id ? "Membuat…" : "🔗 Link Baru"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
